import SizesMixin from '@/mixins/Sizes.js'
import ScrollMixin from '@/mixins/Scroll.js'
import TimeMixin from '@/mixins/Time.js'
import EventHubMixin from '@/mixins/EventHub.js'
import { timingSafeEqual } from 'crypto';

export default
{
    name: 'app-picture',

    mixins:
    [
        SizesMixin,
        ScrollMixin,
        TimeMixin,
        EventHubMixin
    ],

    props:
    {
        active: { type: Boolean, default: true }, // Is active (should load)
        animated: { type: Boolean, default: true }, // Should fade when loader
        maxPixelRatio: { type: Number, default: 1.5 }, // Limit de pixel ratio
        autoSize: { type: Boolean, default: true }, // Should update on window resize
        resizeThrottle: { type: Number, default: 200 }, // Throttle the resize event to prevent to much triggers
        width: { type: Number, default: 0 }, // Width (if no autoSize)
        height: { type: Number, default: 0 }, // height (if no autoSize)
        scale: { type: Number, default: 1 }, // Simple size multiplier
        sources: { type: String, default: '' }, // Sources 'http://domain.com/image-1.jpg 200x200, http://domain.com/image-2.jpg 400x400'
        downScale: { type: Boolean, default: false }, // Should load image even if it's smaller
        quality: { type: String, default: 'closest' }, // Algorithm to find the most suitable source ('strictly-below' | 'below' | 'closest' | 'above' | 'strictly-above')
        hasParallax: { type: Boolean, default: true }, // Add scroll parallax
        parallaxStrength: { type: Number, default: 0.15 }, // Parallax strength
        elementStyle: { type: Object, default()
        {
            return {}
        } }
    },

    watch:
    {
        active()
        {
            if(this.active)
            {
                this.resize()
            }
        },

        width()
        {
            this.resize()
        },

        height()
        {
            this.resize()
        },

        scale()
        {
            this.resize()
        },

        elementStyle()
        {
            for(const _styleProperty in this.elementStyle)
            {
                this.images.$a.style[_styleProperty] = this.elementStyle[_styleProperty]
                this.images.$b.style[_styleProperty] = this.elementStyle[_styleProperty]
            }
        }
    },

    created()
    {
        this.size = {}
        this.size.width = this.width
        this.size.height = this.height

        // Set resolutions
        this.resolutions = {}
        this.resolutions.previous = null
        this.resolutions.current = null
        this.resolutions.all = []

        const sources = this.sources.split(',')

        for(const _source of sources)
        {
            const source = _source.trim()
            const sourceParts = source.split(/\s+/)

            if(sourceParts.length === 2)
            {
                const sizeParts = sourceParts[1].split('x')

                if(sizeParts.length === 2)
                {
                    const resolution = {}
                    resolution.source = sourceParts[0]
                    resolution.width = parseInt(sizeParts[0])
                    resolution.height = parseInt(sizeParts[1])

                    this.resolutions.all.push(resolution)
                }
            }
        }

        this.throttleTimeout = null
        this.pixelRatio = Math.min(window.devicePixelRatio, this.maxPixelRatio)
    },

    mounted()
    {
        this.images = {}
        this.images.current = 'a'
        this.images.$a = this.$refs.imageA
        this.images.$b = this.$refs.imageB
        this.images.loader = {}
        this.images.loader.loading = false
        this.images.loader.$image = new Image()

        // Apply style
        for(const _styleProperty in this.elementStyle)
        {
            this.images.$a.style[_styleProperty] = this.elementStyle[_styleProperty]
            this.images.$b.style[_styleProperty] = this.elementStyle[_styleProperty]
        }

        this.images.$a.style.backgorundPosition = 'center center'

        this.images.loader.$image.addEventListener('load', () =>
        {
            this.images.loader.loading = false

            // Change and show B
            if(this.images.current === 'a')
            {
                this.images.$b.style.backgroundImage = `url(${this.images.loader.$image.src})`
                this.images.$b.classList.add('visible')
                this.images.current = 'b'
            }

            // Change A and hide B to reveal A
            else
            {
                this.images.$a.style.backgroundImage = `url(${this.images.loader.$image.src})`
                this.images.$b.classList.remove('visible')
                this.images.current = 'a'
            }

            // Loader
            this.$eventHub.$emit('loader.remove')

            this.$emit('loading-end')
            this.$emit('changed')
        })

        this.images.loader.$image.addEventListener('error', () =>
        {
            this.images.loader.loading = false

            // Loader
            this.$eventHub.$emit('loader.remove')

            this.$emit('loading-error')
        })

        // Has parallax
        if(this.hasParallax)
        {
            this.parallax = {}
            this.parallax.$element = this.$refs.parallax
            this.parallax.boundings = {}
            this.parallax.boundings.top = 0
            this.parallax.boundings.center = 0
            this.parallax.boundings.bottom = 0

            this.parallax.value = 0
            this.parallax.rounded = 0

            this.parallax.update = () =>
            {
                this.parallax.value = (this.scroll.current - this.parallax.boundings.center + this.sizes.height * 0.5) * this.parallaxStrength
            }
        }

        this.onResize()
        this.onScroll()
    },

    methods:
    {
        onScroll()
        {
            if(this.hasParallax)
            {
                this.parallax.update()
            }
        },

        onTick()
        {
            if(this.hasParallax)
            {
                const rounded = Math.round(this.parallax.value * 10) / 10
                if(rounded !== this.parallax.rounded)
                {
                    this.parallax.rounded = rounded
                    this.parallax.$element.style.transform = `translateY(${this.parallax.rounded}px)`
                }
            }
        },

        onResize()
        {
            // No auto size
            if(!this.autoSize)
            {
                return
            }

            // Throttle
            if(this.resizeThrottle > 0)
            {
                if(this.throttleTimeout)
                {
                    window.clearTimeout(this.throttleTimeout)
                    this.throttleTimeout = null
                }

                this.throttleTimeout = window.setTimeout(() =>
                {
                    this.resize()
                    this.throttleTimeout = null
                }, this.resizeThrottle)
            }

            // No throttle
            else
            {
                this.resize()
            }
        },

        resize()
        {
            // Inactive
            if(!this.active)
            {
                return
            }

            let boundings = null

            // Pixel ratio
            this.pixelRatio = Math.min(window.devicePixelRatio, this.maxPixelRatio)

            // Auto size
            // Get element size
            if(this.autoSize)
            {
                boundings = this.$el.getBoundingClientRect()

                this.size.width = boundings.width
                this.size.height = boundings.height
            }

            // No auto size
            // Use width and height props
            else
            {
                this.size.width = this.width
                this.size.height = this.height
            }

            // Test if width and height
            if(this.size.width > 0 && this.size.height > 0)
            {
                // Calculate effective width and height using pixel ratio and scale
                this.size.effectiveWidth = this.size.width * this.pixelRatio * this.scale
                this.size.effectiveHeight = this.size.height * this.pixelRatio * this.scale

                // Calculate score
                for(const resolution of this.resolutions.all)
                {
                    resolution.score = (resolution.width / this.size.effectiveWidth) * (resolution.height / this.size.effectiveHeight)
                }

                // Try to update resolution
                this.updateResolution()
            }

            // Parallax
            if(this.hasParallax)
            {
                if(!boundings)
                {
                    boundings = this.$el.getBoundingClientRect()
                }

                this.parallax.boundings.top = boundings.top + this.scroll.current
                this.parallax.boundings.center = this.parallax.boundings.top + boundings.height * 0.5
                this.parallax.boundings.bottom = this.parallax.boundings.top + boundings.height

                this.parallax.update()
            }
        },

        updateResolution()
        {
            let bestResolution = null

            // Test which algorithm to use
            switch(this.quality)
            {
                case 'strictly-below':
                    this.resolutions.all.sort((_a, _b) => _a.score - _b.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(resolution.score < 1 && resolution.width < this.size.effectiveWidth && resolution.height < this.size.effectiveHeight)
                        {
                            bestResolution = resolution
                        }
                    }

                    // Fallback classic 'below'
                    if(bestResolution === null)
                    {
                        for(const resolution of this.resolutions.all)
                        {
                            if(bestResolution === null || resolution.score < 1)
                            {
                                bestResolution = resolution
                            }
                        }
                    }

                    break

                case 'below':
                    this.resolutions.all.sort((_a, _b) => _a.score - _b.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(bestResolution === null || resolution.score < 1)
                        {
                            bestResolution = resolution
                        }
                    }

                    break

                case 'above':
                    this.resolutions.all.sort((_a, _b) => _b.score - _a.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(bestResolution === null || resolution.score > 1)
                        {
                            bestResolution = resolution
                        }
                    }

                    break

                case 'strictly-above':
                    this.resolutions.all.sort((_a, _b) => _b.score - _a.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(resolution.score > 1 && resolution.width > this.size.effectiveWidth && resolution.height > this.size.effectiveHeight)
                        {
                            bestResolution = resolution
                        }
                    }

                    // Fallback classic 'above'
                    if(bestResolution === null)
                    {
                        for(const resolution of this.resolutions.all)
                        {
                            if(bestResolution === null || resolution.score > 1)
                            {
                                bestResolution = resolution
                            }
                        }
                    }

                    break

                case 'closest':
                    this.resolutions.all.sort((_a, _b) => Math.abs(_a.score - 1) - Math.abs(_b.score - 1))
                    bestResolution = this.resolutions.all[0]

                    break
            }

            // Resolution changed
            if(bestResolution !== this.resolutions.current)
            {
                // No current resolutions OR should downscale OR new resolution scale is bigger
                if(this.resolutions.current === null || this.downScale || bestResolution.width * bestResolution.height > this.resolutions.current.width * this.resolutions.current.height)
                {
                    // Same as previous
                    if(bestResolution === this.resolutions.previous)
                    {
                        // Just toggle
                        // Change and show B
                        if(this.images.current === 'a')
                        {
                            this.images.$b.classList.add('visible')
                            this.images.current = 'b'
                        }

                        // Change A and hide B to reveal A
                        else
                        {
                            this.images.$b.classList.remove('visible')
                            this.images.current = 'a'
                        }

                        this.$emit('changed')
                    }

                    // Different than previous
                    else
                    {
                        // Abort current loading
                        if(this.images.loader.loading)
                        {
                            this.images.loader.$image.src = ''

                            this.$emit('loading-abort')
                        }

                        // Start loading
                        this.images.loader.loading = true
                        this.images.loader.$image.src = bestResolution.source

                        // Loader
                        this.$eventHub.$emit('loader.add')

                        this.$emit('change')
                        this.$emit('loading-start')
                    }

                    this.resolutions.previous = this.resolutions.current
                    this.resolutions.current = bestResolution
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-picture/script.js